import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, isCancelledUpload, type UploadProgress } from './api.js';

export interface ScriptSubmitted {
  scriptVersionId: string;
  pipelineRunId: string;
  created: boolean;
}

/**
 * THE TWO WAYS A SCREENPLAY GETS IN — a file or pasted text — wherever they
 * are asked.
 *
 * Step 1 and the Script tab both submit a screenplay and both need the same
 * four answers about it: is a file on its way and how far has it got, can it
 * be stopped, was it refused and why, and did it land. Two copies of that were
 * one edit away from disagreeing about which caches a new screenplay makes
 * stale.
 *
 * WHAT HAPPENS AFTER A SCREENPLAY LANDS stays the caller's (`onSubmitted`):
 * one surface stays put and shows the reading, the other shows the scenes.
 *
 * Submitting only stores the screenplay and queues its parse, which is free.
 * Nothing here asks for the AI breakdown — that is a separate button.
 */
export function useScriptSubmission(projectId: string, onSubmitted: (result: ScriptSubmitted) => void) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const controller = useRef<AbortController | null>(null);
  // Read through a ref so a caller can pass a fresh closure every render.
  const submitted = useRef(onSubmitted);
  submitted.current = onSubmitted;

  const landed = async (result: ScriptSubmitted) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['current-script', projectId] }),
    ]);
    submitted.current(result);
  };

  const upload = useMutation({
    mutationFn: (file: File) => {
      const abort = new AbortController();
      controller.current = abort;
      setProgress({ loaded: 0, total: file.size });

      return api.uploadScript(projectId, file, { onProgress: setProgress, signal: abort.signal });
    },
    onSuccess: landed,
    onSettled: () => {
      controller.current = null;
      setProgress(null);
    },
  });

  const paste = useMutation({
    mutationFn: (text: string) => api.pasteScript(projectId, text),
    onSuccess: landed,
  });

  const failure = upload.error ?? paste.error;

  /**
   * A refusal the screen can explain. A cancelled upload is not one — the
   * producer stopped it — and a network failure from `fetch` arrives as a bare
   * TypeError, which used to render as nothing at all.
   */
  const refusal =
    failure === null || isCancelledUpload(failure)
      ? null
      : failure instanceof ApiError
        ? failure
        : new ApiError(0, null, 'The screenplay did not reach the server. Check your connection and try again.');

  return {
    upload: (file: File) => {
      paste.reset();
      upload.mutate(file);
    },
    cancelUpload: () => {
      controller.current?.abort();
    },
    /**
     * Send the same file again — offered only when the failure was the network
     * or the server, never a refusal of the file itself, which would only be
     * refused again.
     */
    retryUpload:
      upload.isError && upload.variables !== undefined && refusal !== null && (refusal.status === 0 || refusal.status >= 500)
        ? () => {
            const again = upload.variables;
            if (again !== undefined) upload.mutate(again);
          }
        : null,
    uploading: upload.isPending,
    /** The file on its way, for the progress row; null when nothing is uploading. */
    uploadingFile: upload.isPending ? (upload.variables ?? null) : null,
    progress,
    paste: (text: string) => {
      upload.reset();
      paste.mutate(text);
    },
    pasting: paste.isPending,
    refusal,
    /** Byte-for-byte a screenplay already on the production: the existing version was reused. */
    duplicate: (upload.data ?? paste.data)?.created === false,
    reset: () => {
      upload.reset();
      paste.reset();
    },
  };
}
